import { EonUiButton } from "@eon-ui/eon-ui-components-react";
import Modal from "../../../components/modal/Modal";
import { ReactNode, useState } from "react";
import {
    OnboardingActivityAction,
    OnboardingActivityChange,
    OnboardingActivityDiff,
    OnboardingActivityEntry,
    OnboardingAppData,
    OnboardingQnAData,
} from "../OnboardingPortal.types";

const HIDDEN_FIELDS = new Set([
    "updatedAt",
    "etag",
    "ts",
    "_etag",
    "_ts",
    "_rid",
    "_self",
    "_attachments",
    "activities",
    "appData",
    "qnaData",
]);

const ICON_FIELDS = new Set(["iconDataUrl"]);

const FIELD_LABELS: Record<string, string> = {
    answer: "Answer",
    appId: "App ID",
    category: "Category",
    companiesAllowed: "Companies allowed",
    companiesRestricted: "Companies restricted",
    createdAt: "Created at",
    id: "Record ID",
    keywords: "Keywords",
    owner: "Owner",
    question: "Question",
    reviewerNote: "Reviewer note",
    status: "Status",
    subTitle: "Subtitle",
    title: "Title",
    tooltip: "Tooltip",
    updatedAt: "Updated at",
    url: "URL",
};

const APP_SNAPSHOT_FIELDS = ["title", "appId", "status", "owner"];
const QNA_SNAPSHOT_FIELDS = ["question", "status", "owner"];

type ActivityEntityType = "app" | "qna";

type ActivityLogScreenProps = {
    entityType: ActivityEntityType;
    entityData: OnboardingAppData | OnboardingQnAData | null;
};

const isObjectRecord = (
    value: unknown
): value is Record<string, unknown> => typeof value === "object" && value !== null;

const isActivityDiff = (value: unknown): value is OnboardingActivityDiff => {
    if (!isObjectRecord(value)) {
        return false;
    }

    return "changes" in value || "old" in value || "new" in value;
};

const formatFieldLabel = (fieldName: string): string => {
    if (FIELD_LABELS[fieldName]) {
        return FIELD_LABELS[fieldName];
    }

    return fieldName
        .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
        .replace(/[_-]/g, " ")
        .replace(/^./, (value) => value.toUpperCase());
};

const isHiddenField = (fieldName: string): boolean => {
    return HIDDEN_FIELDS.has(fieldName);
};

const isIconField = (fieldName: string): boolean => {
    return ICON_FIELDS.has(fieldName);
};

const isImageValue = (value: unknown): value is string => {
    return (
        typeof value === "string" &&
        value.trim().length > 0 &&
        (value.startsWith("data:image/") || value.startsWith("http"))
    );
};

const formatValue = (value: unknown): string => {
    if (value === null || value === undefined) {
        return "Not provided";
    }

    if (typeof value === "string") {
        return value.trim() || "Not provided";
    }

    if (typeof value === "number" || typeof value === "boolean") {
        return String(value);
    }

    if (Array.isArray(value)) {
        return value.length > 0 ? value.map((item) => formatValue(item)).join(", ") : "None";
    }

    if (isObjectRecord(value)) {
        try {
            return JSON.stringify(value, null, 2);
        } catch {
            return "[Object]";
        }
    }

    return String(value);
};

const renderValue = (fieldName: string, value: unknown): ReactNode => {
    if (isIconField(fieldName) && isImageValue(value)) {
        return (
            <span className="activity-log__value-image-wrap">
                <img
                    className="activity-log__value-image"
                    src={value}
                    alt={`${formatFieldLabel(fieldName)} preview`}
                />
            </span>
        );
    }

    return formatValue(value);
};

const formatTimestamp = (value?: string): string => {
    if (!value) {
        return "Time unavailable";
    }

    const timestamp = new Date(value);
    if (Number.isNaN(timestamp.getTime())) {
        return value;
    }

    return timestamp.toLocaleString(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
    });
};

const getActivityAction = (
    activityType?: string
): OnboardingActivityAction | "unknown" => {
    const action = activityType?.split("-").at(1);
    if (action === "created" || action === "updated" || action === "reviewed") {
        return action;
    }

    return "unknown";
};

const getActivityLabel = (
    action: OnboardingActivityAction | "unknown"
): string => {
    switch (action) {
        case "created":
            return "Created";
        case "updated":
            return "Updated";
        case "reviewed":
            return "Reviewed";
        default:
            return "Activity";
    }
};

const getActivityHeadline = (
    entityType: ActivityEntityType,
    action: OnboardingActivityAction | "unknown"
): string => {
    const entityLabel = entityType === "app" ? "application" : "QnA entry";

    switch (action) {
        case "created":
            return `Submitted ${entityLabel}`;
        case "updated":
            return `Updated ${entityLabel}`;
        case "reviewed":
            return `Reviewed ${entityLabel}`;
        default:
            return `Changed ${entityLabel}`;
    }
};

const getActivityPayload = (
    activity: OnboardingActivityEntry,
    entityType: ActivityEntityType
): Record<string, unknown> | OnboardingActivityDiff | undefined => {
    return entityType === "app" ? activity.appData : activity.qnaData;
};

const getSnapshotFields = (
    entityType: ActivityEntityType,
    payload: Record<string, unknown>
): Array<[string, unknown]> => {
    const preferredFields =
        entityType === "app" ? APP_SNAPSHOT_FIELDS : QNA_SNAPSHOT_FIELDS;

    const prioritizedFields = preferredFields
        .filter((fieldName) => payload[fieldName] !== undefined)
        .map((fieldName) => [fieldName, payload[fieldName]] as [string, unknown]);

    if (prioritizedFields.length > 0) {
        return prioritizedFields;
    }

    return Object.entries(payload)
        .filter(([fieldName]) => !isHiddenField(fieldName))
        .slice(0, 4);
};

const ActivityLogScreen = ({
    entityType,
    entityData,
}: ActivityLogScreenProps) => {
    const [showActivityLog, setShowActivityLog] = useState(false);

    const toggleModal = () => setShowActivityLog((prev) => !prev);

    const entityTitle: string =
        entityType === "app"
            ? (entityData?.title as string) || (entityData?.appId as string) || "Application"
            : (entityData?.question as string) || "QnA entry";

    const activities = [...(entityData?.activities ?? [])].sort((left, right) => {
        const leftTime = Date.parse(left.updatedAt || left.createdAt || "");
        const rightTime = Date.parse(right.updatedAt || right.createdAt || "");
        return rightTime - leftTime;
    });

    return (
        <>
            <EonUiButton
                icon="clock"
                rank="functional"
                onClick={() => setShowActivityLog(!showActivityLog)}
            />
            <Modal
                toggleModal={showActivityLog}
                closeModal={toggleModal}
                isCloseIcon={true}
                closeOnBackdrop={false}
                modalClass="activity-log-modal"
            >
                <div className="activity-log">
                    <div className="activity-log__header">
                        <p className="activity-log__description">{entityTitle}</p>
                    </div>

                    {activities.length === 0 ? (
                        <div className="activity-log__empty-state">
                            No activity has been recorded for this item yet.
                        </div>
                    ) : (
                        <div className="activity-log__timeline">
                            {activities.map((activity, index) => {
                                const action = getActivityAction(activity.activity);
                                const payload = getActivityPayload(activity, entityType);
                                const diffPayload =
                                    payload && isActivityDiff(payload) ? payload : undefined;
                                const snapshotPayload =
                                    payload && !isActivityDiff(payload) && isObjectRecord(payload)
                                        ? payload
                                        : undefined;
                                const changes = Object.entries(diffPayload?.changes ?? {}).filter(
                                    ([fieldName]) => !isHiddenField(fieldName)
                                );
                                const snapshotFields = snapshotPayload
                                    ? getSnapshotFields(entityType, snapshotPayload)
                                    : [];

                                return (
                                    <div
                                        key={activity.id || `${activity.activity}-${index}`}
                                        className={`activity-log__item activity-log__item--${action} ${index === activities.length - 1 ? "is-last" : ""}`}
                                    >
                                        <div className="activity-log__marker">
                                            <span className="activity-log__dot" />
                                            <span className="activity-log__line" />
                                        </div>
                                        <div className="activity-log__card">
                                            <div className="activity-log__card-top">
                                                <div>
                                                    <span className={`activity-log__badge activity-log__badge--${action}`}>
                                                        {getActivityLabel(action)}
                                                    </span>
                                                    <h2 className="activity-log__card-title">
                                                        {getActivityHeadline(entityType, action)}
                                                    </h2>
                                                </div>
                                                <div className="activity-log__meta">
                                                    <span>
                                                        {formatTimestamp(
                                                            activity.updatedAt ||
                                                                activity.createdAt
                                                        )}
                                                    </span>
                                                    <span>
                                                        By {formatValue(activity.owner)}
                                                    </span>
                                                </div>
                                            </div>

                                            {changes.length > 0 ? (
                                                <div className="activity-log__changes-grid">
                                                    {changes.map(([fieldName, change]) => {
                                                        const activityChange =
                                                            change as OnboardingActivityChange;

                                                        return (
                                                            <div
                                                                key={fieldName}
                                                                className="activity-log__change-card"
                                                            >
                                                                <div className="activity-log__change-field">
                                                                    {formatFieldLabel(fieldName)}
                                                                </div>
                                                                <div className="activity-log__change-values">
                                                                    <div className="activity-log__value-block">
                                                                        <span>Before</span>
                                                                        <div className="activity-log__value-text">
                                                                            {renderValue(
                                                                                fieldName,
                                                                                activityChange.old
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                    <div className="activity-log__value-block">
                                                                        <span>After</span>
                                                                        <div className="activity-log__value-text">
                                                                            {renderValue(
                                                                                fieldName,
                                                                                activityChange.new
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            ) : snapshotFields.length > 0 ? (
                                                <div className="activity-log__snapshot-grid">
                                                    {snapshotFields.map(([fieldName, value]) => (
                                                        <div
                                                            key={fieldName}
                                                            className="activity-log__snapshot-card"
                                                        >
                                                            <span>{formatFieldLabel(fieldName)}</span>
                                                            <strong>{renderValue(fieldName, value)}</strong>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <div className="activity-log__empty-detail">
                                                    No field-level details were captured for this event.
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </Modal>
        </>
    );
};

export default ActivityLogScreen;
